import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { useRef } from 'react';
import homeFill from '@iconify/icons-eva/home-fill';
import personFill from '@iconify/icons-eva/person-fill';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import {
  generatePath,
  Link as RouterLink,
  useNavigate,
} from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// material
import { alpha } from '@mui/material/styles';
import { Button, Box, Divider, MenuItem, Typography } from '@mui/material';
// hooks
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import { toggleCustomerMode } from '../../../../redux/slices/ui';
// components
import { MIconButton } from 'src/minimals/@material-extend';
import MyAvatar from 'src/minimals/MyAvatar';
import MenuPopover from 'src/minimals/MenuPopover';
import { PATH_ADMIN, PATH_PAGE, PATH_PROFILE, PATH_WORKSPACE } from 'src/routes/paths';
import {
  currentWorkspaceSelectors,
  uiSelectors,
  userSelectors,
  workspaceSelectors,
} from 'src/selectors';
import { useTranslation } from 'react-i18next';
import { useModal } from 'src/hooks/useModal';
import { REPORTER_ROLES_BY_RESOURCE } from 'src/constants';

// ----------------------------------------------------------------------

const MENU_OPTIONS = (workspaceId = '', isLoggedAsCustomer) => [
  {
    label: 'account.popover.home',
    icon: homeFill,
    linkTo: '/',
  },
  {
    label: 'account.popover.profile',
    icon: personFill,
    linkTo: PATH_PROFILE.root,
  },
  ...(workspaceId && !isLoggedAsCustomer
    ? [
        {
          label: 'account.popover.settings',
          icon: settings2Fill,
          linkTo: generatePath(PATH_WORKSPACE.settings.general, {
            workspaceId,
          }),
        },
      ]
    : []),
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const workspace = useSelector(currentWorkspaceSelectors.getCurrentWorkspace);
  const dispatch = useDispatch();

  const isMountedRef = useIsMountedRef();
  const user = useSelector(userSelectors.getUser());
  const [open, openModal, closeModal] = useModal();
  const { t } = useTranslation();
  const isLoggedAsCustomer = useSelector(uiSelectors.isLoggedAsCustomer);

  const isCustomerMember = user.userRoles.some(
    (role) =>
      role.customer ||
      (role.device && role.role.id !== REPORTER_ROLES_BY_RESOURCE.DEVICE)
  );

  // Check if user has system-level role (systemId = 1)
  const hasAdminAccess = user?.userRoles?.some((ur) => ur.systemId === 1);

  const handleLogout = async () => {
    try {
      navigate('/logout');
      if (isMountedRef.current) {
        closeModal();
      }
    } catch (error) {
      enqueueSnackbar(t('unable.to.logout'), { variant: 'error' });
    }
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={openModal}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
            },
          }),
        }}
      >
        <MyAvatar avatarSource={user.profilePicture} name={user.firstName} />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={closeModal}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {user.firstName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {MENU_OPTIONS(workspace?.id, isLoggedAsCustomer).map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={closeModal}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box
              component={Icon}
              icon={option.icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24,
              }}
            />

            {t(option.label)}
          </MenuItem>
        ))}
        {isCustomerMember && (
          <MenuItem
            sx={{ typography: 'body2', py: 1, px: 2.5, whiteSpace: 'normal' }}
            to={isLoggedAsCustomer ? PATH_PAGE.workspaces : PATH_PAGE.customer}
            component={RouterLink}
            onClick={() => dispatch(toggleCustomerMode())}
          >
            <WorkOutlineIcon
              sx={{
                mr: 2,
                width: 24,
                height: 24,
              }}
            />
            {t(
              isLoggedAsCustomer
                ? 'account_popover.workspace_user'
                : 'account_popover.customer_user'
            )}
          </MenuItem>
        )}
        {hasAdminAccess && (
          <MenuItem
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
            onClick={() => {
              window.open(PATH_ADMIN.root, '_blank');
              closeModal();
            }}
          >
            <AdminPanelSettingsIcon
              sx={{
                mr: 2,
                width: 24,
                height: 24,
              }}
            />
            {t('account_popover.admin_mode')}
          </MenuItem>
        )}
        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button
            fullWidth
            color="inherit"
            variant="outlined"
            onClick={handleLogout}
          >
            {t('account.popover.logout')}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
